import React, { createContext, useState, useContext, useEffect } from 'react';
import config from '../config';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [csrfToken, setCsrfToken] = useState('');

  // Fetch CSRF token on initial load
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // First, get CSRF token
        await fetchCsrfToken();
        
        // Then check if user is already logged in
        await checkAuthStatus();
      } catch (error) {
        console.error('Auth initialization failed:', error);
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const fetchCsrfToken = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/auth/csrf/`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        // CSRF cookie should be set automatically by Django
        const token = getCookie('csrftoken');
        setCsrfToken(token);
        return token;
      }
    } catch (error) {
      console.error('Failed to fetch CSRF token:', error);
    }
    return null;
  };

  const getCookie = (name) => {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  };

  const checkAuthStatus = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/auth/status/`, {
        credentials: 'include',
        headers: {
          'X-CSRFToken': csrfToken,
        },
      });
      
      const data = await response.json();
      
      if (data.is_authenticated) {
        setUser({
          username: data.username,
          email: data.email,
        });
      }
    } catch (error) {
      console.error('Failed to check auth status:', error);
    }
  };

  const login = async (username, password) => {
    try {
      // Ensure we have a CSRF token before login
      let token = csrfToken;
      if (!token) {
        token = await fetchCsrfToken();
      }

      const response = await fetch(`${config.API_BASE_URL}/api/auth/login/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': token,
        },
        credentials: 'include',
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (response.ok) {
        // Get new CSRF token after login
        const newToken = getCookie('csrftoken');
        setCsrfToken(newToken);
        
        setUser({
          username: data.username,
        });
        
        return { success: true };
      } else {
        return { success: false, error: data.message || 'Login failed' };
      }
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      await fetch(`${config.API_BASE_URL}/api/auth/logout/`, {
        method: 'POST',
        headers: {
          'X-CSRFToken': csrfToken,
        },
        credentials: 'include',
      });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setCsrfToken('');
    }
  };

  const value = {
    user,
    loading,
    csrfToken,
    login,
    logout,
    isAuthenticated: !!user,
    getCookie, // Expose for components to use
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};